import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { SeatPublic, TableConfigDto, TableInfo } from '@cardhub/protocol';
import { api } from '../api/client';
import { GameArt } from '../cards/PlayingCard';
import { Avatar, Coins, ErrorState, Icon, Loading, TopBar } from '../components/ui';
import { useFormat, useT } from '../i18n';
import { useAsync } from '../lib/hooks';
import { filterGames } from '../lib/gameSearch';
import { loadGame, loadGames } from '../offline/catalog';
import { useGame } from '../store/game';
import { useSession } from '../store/session';
import { GAME_ART } from './Home';

const GAMES = ['CALL_BREAK', 'SPADES', 'TARNEEB', 'HEARTS', 'FIVE_HUNDRED', 'JUDGEMENT', 'RUMMY', 'EUCHRE', 'GIN_RUMMY', 'TWENTY_NINE', 'CRAZY_EIGHTS', 'CRIBBAGE', 'BLACKJACK', 'BHABHI', 'PINOCHLE', 'ANDAR_BAHAR', 'BRIDGE', 'SEVENS', 'SCOPA', 'BACCARAT', 'BLUFF', 'DRAGON_TIGER', 'FLUSH', 'COURT_PIECE', 'DEHLA_PAKAD', 'WHIST', 'SEEP', 'BELOTE', 'DURAK', 'PRESIDENT', 'BRISCOLA', 'CASINO_WAR', 'THREE_CARD_POKER', 'GO_FISH', 'TRESSETTE', 'RED_DOG', 'CANASTA', 'SKAT', 'PAI_GOW', 'BIG_TWO', 'WAR', 'RUMMY_500', 'BRAG', 'PITCH', 'HAZARI', 'TEEN_PATTI', 'POKER', 'MARRIAGE'] as const;

export function GameTables() {
  const { type = 'CALL_BREAK' } = useParams();
  const t = useT();
  const f = useFormat();
  const nav = useNavigate();
  const balance = useSession((s) => s.balance);
  const quickPlay = useGame((s) => s.quickPlay);
  const game = useAsync(() => loadGame(type), [type]);
  const all = useAsync(() => loadGames(), []);
  const [query, setQuery] = useState('');
  const nameOf = (g: string) => t(`game.${g}` as 'game.CALL_BREAK');
  // Same order as the Home list; the static list only covers the moment before the catalog arrives.
  const ordered: { type: string }[] = all.data ?? GAMES.map((g) => ({ type: g }));
  // Typing opens the best match straight away, so the tables below are always for the game you searched.
  const search = (q: string) => {
    setQuery(q);
    const first = q.trim() ? filterGames(ordered, q, nameOf)[0] : undefined;
    if (first && first.type !== type && !filterGames([{ type }], q, nameOf).length) nav(`/games/${first.type}`, { replace: true });
  };
  const chips = query.trim() ? filterGames(ordered, query, nameOf) : ordered;

  const describe = (c: TableConfigDto) => {
    const r = c.rules as Record<string, number | string>;
    if (c.gameType === 'COURT_PIECE') return `Partners · first to ${r.targetScore ?? 5} courts · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'DEHLA_PAKAD') return `Partners · win the tens · first to ${r.targetScore ?? 5} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'BRISCOLA') return `Partners · 120 points in the pack · first to ${r.targetScore ?? 1} ${(r.targetScore ?? 1) === 1 ? 'hand' : 'hands'} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'TRESSETTE') return `Partners · no trumps, follow suit · first to ${r.targetScore ?? 21} points · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'SKAT') return `Three players · one declarer · first to ${r.targetScore ?? 100} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'BELOTE') return `Partners · first to ${r.targetScore ?? 501} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'PITCH') return `Partners · high, low, jack and game · first to ${r.targetScore ?? 11} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'RUMMY_500') return `Four players · meld and lay off · first to ${r.targetScore ?? 500} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'CANASTA') return `Partners · melds and canastas · first to ${r.targetScore ?? 5000} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'WHIST') return `Partners · first to ${r.targetScore ?? 10} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'BLUFF') return `Bluff and call bluffs · first out wins · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'RED_DOG') return `Ante ${r.minBet}–${r.maxBet} · Bet the third card lands between · Up to 5:1`;
    if (c.gameType === 'CASINO_WAR') return `Bets ${r.minBet}–${r.maxBet} · High card wins 1:1 · Tie bet 10:1`;
    if (c.gameType === 'DRAGON_TIGER') return `Bets ${r.minBet}–${r.maxBet} · Dragon 1:1 · Tiger 1:1 · Tie ${r.tiePays ?? 11}:1`;
    if (c.gameType === 'PAI_GOW') return `Bets ${r.minBet}–${r.maxBet} · Set a 5-card and a 2-card hand · Win both 1:1 (5% commission)`;
    if (c.gameType === 'THREE_CARD_POKER') return`Ante ${r.minBet}–${r.maxBet} · Ante bonus up to 5:1 · Pair Plus up to 40:1`;
    if (c.gameType === 'BACCARAT') return `Bets ${r.minBet}–${r.maxBet} · Banker 0.95:1 · Tie 8:1`;
    if (c.gameType === 'SEEP') return `Partnership · houses and seeps · first to ${r.targetScore ?? 100} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'SCOPA') return `Partnership · first to ${r.targetScore ?? 11} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'SEVENS') return `Shed your cards from the sevens out · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'BRIDGE') return `Chicago · ${r.deals ?? 4} deals · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'ANDAR_BAHAR') return `Bets ${r.minBet}–${r.maxBet} · Andar 0.9:1 · Bahar 1:1`;
    if (c.gameType === 'PINOCHLE') return `Partnership · first to ${r.targetScore ?? 150} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'WAR') return `Two players · flip, win the cards, go to war on ties · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'GO_FISH') return `Ask for ranks · collect books of four · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'HAZARI') return `Win tricks for card points · first to ${r.targetScore ?? 500} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'BIG_TWO') return `Climb with singles, pairs, triples and five-card hands · twos are high · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'PRESIDENT') return `Shed your cards · first out is President · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'DURAK') return `Beat the attack with a higher card or a trump · last holder loses · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'BHABHI') return `Shed your cards · last holder loses · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'BLACKJACK') return `Bets ${r.minBet}–${r.maxBet} · 3:2 blackjack`;
    if (c.gameType === 'FLUSH') return `Boot ${r.boot} · Colour beats sequence`;
    if (c.gameType === 'BRAG') return `Boot ${r.boot} · Prial, run, flush · 3-3-3 is the top prial`;
    if (c.gameType === 'TEEN_PATTI') return `Boot ${r.boot} · ${r.variant === 'muflis' ? 'Muflis' : 'Classic'}`;
    if (c.gameType === 'POKER') return `${r.variant === 'PLO' ? 'Pot-Limit Omaha' : "No-Limit Hold'em"} · ${r.smallBlind}/${r.bigBlind}`;
    if (c.gameType === 'MARRIAGE') return `Point value ${r.pointValue}`;
    if (c.gameType === 'CRIBBAGE') return `Head-to-head · race to ${r.targetScore ?? 121} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'CRAZY_EIGHTS') return `Shed your hand · first to ${r.targetScore ?? 150} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'TWENTY_NINE') return `Partnership · first to +${r.targetScore ?? 6} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'GIN_RUMMY') return `Head-to-head · first to ${r.targetScore ?? 100} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'EUCHRE') return `Partners · first to ${r.targetScore ?? 10} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'RUMMY') return `13 cards · ${r.deals ?? 1} ${(r.deals ?? 1) === 1 ? 'deal' : 'deals'} · lowest points win · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'JUDGEMENT') return `Everyone for themselves · up to ${r.maxCards ?? 7} cards · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'FIVE_HUNDRED') return `Partners · first to ${r.targetScore ?? 500} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'HEARTS') return `Every player for themselves · to ${r.targetScore ?? 100} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'TARNEEB') return `Partners · first to ${r.targetScore ?? 41} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    if (c.gameType === 'SPADES') return `Partners · first to ${r.targetScore ?? 500} · ${t('tables.timer', { n: c.turnTimerSec })}`;
    return `${r.rounds ?? 5} rounds · ${t('tables.timer', { n: c.turnTimerSec })}`;
  };

  return (
    <main className="page">
      <TopBar title={t(`game.${type}` as 'game.CALL_BREAK')} onBack={() => nav('/')} right={<button className="icon-btn" onClick={() => nav(`/tutorial/${type}`)} aria-label={t('lobby.howToPlay')}><Icon name="info" /></button>} />
      <div className="game-search" role="search">
        <Icon name="search" size={18} />
        <input
          type="search"
          className="input"
          placeholder={t('games.search')}
          aria-label={t('games.search')}
          value={query}
          onChange={(e) => search(e.target.value)}
          autoComplete="off"
          enterKeyHint="search"
        />
        {query && (
          <button className="clear icon-btn" onClick={() => search('')} aria-label={t('games.clear')}>
            <Icon name="close" size={16} />
          </button>
        )}
      </div>
      <div className="segmented" role="tablist" aria-label={t('lobby.games')} style={{ marginBottom: 16 }}>
        {chips.map(({ type: g }) => (
          <button key={g} aria-pressed={g === type} onClick={() => nav(`/games/${g}`, { replace: true })}>
            {nameOf(g)}
          </button>
        ))}
        {chips.length === 0 && <span className="dim" role="status" style={{ padding: 8 }}>{t('games.noMatch', { q: query.trim() })}</span>}
      </div>
      {game.loading && <Loading />}
      {game.error && <ErrorState message={game.error} onRetry={game.reload} />}
      {game.data && (
        <>
          <div className="game-card" data-game={game.data.type} style={{ minHeight: 140, marginBottom: 20 }}>
            <GameArt cards={GAME_ART[game.data.type]!.slice(0, 3)} />
            <div>
              <h3>{t('tables.title')}</h3>
              <div className="meta">
                <span>{t('common.online', { n: f.compact(game.data.onlinePlayers) })}</span>
                <span>{t('lobby.tables', { n: game.data.activeTables })}</span>
              </div>
            </div>
          </div>
          {!game.data.enabled && <ErrorState message={t('common.comingSoon')} />}
          <div className="col">
            {game.data.tables.map((c) => {
              const need = Math.max(c.minBalance, c.entryFee);
              const tooRich = c.maxBalance !== null && balance > c.maxBalance;
              const canPlay = game.data!.enabled && balance >= need && !tooRich;
              return (
                <div key={c.id} className="card table-tier">
                  <div className="col" style={{ gap: 4 }}>
                    <div className="row" style={{ gap: 8 }}>
                      <strong>{c.name}</strong>
                      {(c.onlinePlayers ?? 0) > 0 && <span className="pill live">{c.onlinePlayers}</span>}
                    </div>
                    <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{describe(c)}</span>
                    <div className="row" style={{ gap: 12, fontSize: 'var(--fs-sm)' }}>
                      <span className="muted">{game.data!.kind === 'match' ? t('tables.entry') : t('tables.buyIn')}</span>
                      <Coins amount={c.entryFee} size={16} />
                      <span className="muted">{t('tables.players', { min: c.minPlayers, max: c.maxPlayers })}</span>
                    </div>
                  </div>
                  <button className="btn primary" disabled={!canPlay} onClick={() => void quickPlay(c.id)} aria-label={`${t('tables.play')} ${c.name}`}>
                    {canPlay ? t('tables.play') : tooRich ? '—' : t('tables.cantAfford', { n: f.compact(need) })}
                  </button>
                </div>
              );
            })}
          </div>
          <button className="btn ghost block" style={{ marginTop: 16 }} onClick={() => nav(`/rooms/new?game=${type}`)}>
            <Icon name="lock" size={18} /> {t('rooms.create')}
          </button>
          <LiveTables game={type} />
        </>
      )}
    </main>
  );
}

interface LiveTable { table: TableInfo; seats: SeatPublic[]; spectators: number; humans: number }

/** Public tables in progress that can be watched (spectator view: no hidden cards). */
function LiveTables({ game }: { game: string }) {
  const t = useT();
  const nav = useNavigate();
  const enabled = useSession((s) => s.flag('SPECTATOR_MODE_ENABLED'));
  const live = useAsync(() => (enabled ? api.get<{ enabled: boolean; tables: LiveTable[] }>(`/api/v1/tables/live?game=${game}`) : Promise.resolve(null)), [enabled, game]);
  if (!enabled || !live.data || live.data.tables.length === 0) return null;
  return (
    <>
      <h2 className="section-title">{t('watch.title')}</h2>
      <div className="list">
        {live.data.tables.map((l) => (
          <button key={l.table.tableId} className="list-item" onClick={() => nav(`/watch/${l.table.tableId}`)} aria-label={`${t('watch.cta')} ${l.table.name}`}>
            <span className="row" style={{ gap: 2 }}>
              {l.seats.filter((s) => s.user).slice(0, 4).map((s) => <Avatar key={s.seat} avatar={s.user!.avatar} size={28} />)}
            </span>
            <span className="grow ellipsis">
              <strong>{l.table.name}</strong>
              <br />
              <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{l.seats.filter((s) => s.user && !s.isBot).map((s) => s.user!.username).join(', ')}</span>
            </span>
            <span className="pill">👁 {l.spectators}</span>
          </button>
        ))}
      </div>
    </>
  );
}
